/**
 * 5-blok — multimedia galereya (ЧТЗ 4.2.11.1, 2.9-band).
 *
 * Fotogalereya (ko'chat ekish jarayonlari, dron monitoringi) va videogalereya
 * (promo-roliklar, o'rgatuvchi videolar). JPG/PNG/MP4; rasmlar backendda
 * avtomatik optimallashtiriladi (eskiz + siqilgan nusxa), ro'yxatda faqat
 * eskiz `loading="lazy"` bilan yuklanadi, to'liq rasm — ko'rish oynasida.
 * Videolar avtomatik ijro etilmaydi va ovoz o'z-o'zidan chiqmaydi.
 */
import { useCallback, useMemo, useState } from "react";
import type { CSSProperties } from "react";

import type { PortalMediaAsset } from "@/shared/api/types";
import { mediaFallback } from "@/shared/data/landing";
import { useI18n } from "@/shared/i18n";
import { useBalancedGrid } from "@/shared/lib/balancedGrid";
import { Icon } from "@/shared/ui/Icon";
import { Modal } from "@/shared/ui/Modal";

import { SplitHead } from "./SplitHead";
import { usePortalMedia } from "./publicData";

type Kind = "photo" | "video";

interface MediaView {
  id: string;
  kind: Kind;
  album: string;
  albumTitle: string;
  title: string;
  caption: string;
  thumb: string;
  full: string;
  player: string;
  width: number;
  height: number;
}

function fromServer(item: PortalMediaAsset): MediaView {
  return {
    id: String(item.id),
    kind: item.kind === "photo" ? "photo" : "video",
    album: item.album_slug || "other",
    albumTitle: item.album_title || "Boshqa",
    title: item.title,
    caption: item.caption,
    thumb: item.thumbnail_url || (item.kind === "photo" ? item.file_url : ""),
    full: item.file_url,
    player: item.player_url,
    width: item.width,
    height: item.height,
  };
}

/** `embedded` — «Yangiliklar» sahifasining pastki qismida (o'z sahifa sarlavhasisiz). */
export function MediaGallery({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const media = usePortalMedia();
  const [kind, setKind] = useState<Kind>("photo");
  const [album, setAlbum] = useState("all");
  const [open, setOpen] = useState<number | null>(null);

  const items: MediaView[] = useMemo(() => {
    if (media.data?.length) return media.data.map(fromServer);
    return mediaFallback.map((item) => ({
      id: item.id,
      kind: item.kind,
      album: item.album,
      albumTitle: item.albumTitle,
      title: item.title,
      caption: "",
      thumb: item.image,
      full: item.image,
      player: "",
      width: 0,
      height: 0,
    }));
  }, [media.data]);

  const ofKind = items.filter((item) => item.kind === kind);
  const albums = useMemo(() => {
    const seen = new Map<string, string>();
    ofKind.forEach((item) => seen.set(item.album, item.albumTitle));
    return Array.from(seen.entries());
  }, [ofKind]);
  const visible = ofKind.filter((item) => album === "all" || item.album === album);
  const current = open === null ? null : visible[open] ?? null;
  const grid = useBalancedGrid(visible.length, { min: 240, gap: 12, minCols: 2 });

  const step = useCallback(
    (delta: number) =>
      setOpen((index) =>
        index === null || visible.length === 0
          ? index
          : (index + delta + visible.length) % visible.length,
      ),
    [visible.length],
  );

  return (
    <section className="section section-alt" id="media" aria-labelledby="media-title">
      <div className="container">
        {embedded ? (
          <div className="media-embedded-head">
            <div>
              <h2 id="media-title">Multimedia galereya</h2>
              <p className="hint">Ko'chat ekish, dron monitoringi va o'rgatuvchi videolar.</p>
            </div>
            <div className="segmented" role="tablist" aria-label="Media turi">
              {(["photo", "video"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={kind === value}
                  aria-pressed={kind === value}
                  onClick={() => {
                    setKind(value);
                    setAlbum("all");
                  }}
                >
                  <Icon name={value === "photo" ? "camera" : "play"} size={14} />
                  {value === "photo" ? "Fotogalereya" : "Videogalereya"}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <SplitHead
            scene="media"
            eyebrow={t("nav.media")}
            title="Multimedia galereya"
            titleId="media-title"
            lead="Ko'chat ekish, dron monitoringi va o'rgatuvchi videolar."
          >
            <div className="segmented" role="tablist" aria-label="Media turi">
              {(["photo", "video"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={kind === value}
                  aria-pressed={kind === value}
                  onClick={() => {
                    setKind(value);
                    setAlbum("all");
                  }}
                >
                  <Icon name={value === "photo" ? "camera" : "play"} size={14} />
                  {value === "photo" ? "Fotogalereya" : "Videogalereya"}
                </button>
              ))}
            </div>
          </SplitHead>
        )}

        <div className="media-body" data-reveal>
        {albums.length > 1 ? (
          <div className="media-albums" role="group" aria-label="Albomlar">
            <button
              type="button"
              className={`chip${album === "all" ? " is-active" : ""}`}
              aria-pressed={album === "all"}
              onClick={() => setAlbum("all")}
            >
              Barchasi
            </button>
            {albums.map(([slug, title]) => (
              <button
                key={slug}
                type="button"
                className={`chip${album === slug ? " is-active" : ""}`}
                aria-pressed={album === slug}
                onClick={() => setAlbum(slug)}
              >
                {title}
              </button>
            ))}
          </div>
        ) : null}

        {visible.length ? (
          <ul className="media-grid" ref={grid.ref as React.RefObject<HTMLUListElement>} style={grid.style}>
            {visible.map((item, index) => (
              <li
                key={item.id}
                style={
                  grid.spanFor(index) > 1
                    ? ({ gridColumn: `span ${grid.spanFor(index)}`, "--span": grid.spanFor(index) } as CSSProperties)
                    : undefined
                }
              >
                <button
                  type="button"
                  className={`media-tile is-${item.kind}`}
                  onClick={() => setOpen(index)}
                  aria-label={`${item.kind === "photo" ? "Rasmni" : "Videoni"} ochish: ${item.title || item.albumTitle}`}
                >
                  {item.thumb ? (
                    <img
                      src={item.thumb}
                      alt={item.title || item.albumTitle}
                      loading="lazy"
                      decoding="async"
                      width={item.width || undefined}
                      height={item.height || undefined}
                    />
                  ) : (
                    <span className="media-placeholder" aria-hidden="true">
                      <Icon name="play" size={28} />
                    </span>
                  )}
                  {item.kind === "video" ? (
                    <span className="media-play" aria-hidden="true">
                      <Icon name="play" size={22} />
                    </span>
                  ) : null}
                  <span className="media-caption">
                    <small>{item.albumTitle}</small>
                    {item.title}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="media-empty">
            <Icon name={kind === "video" ? "play" : "camera"} size={20} />
            {kind === "video"
              ? "Videolar kontent-menejer tomonidan joylanadi — tez orada shu yerda ko'rinadi."
              : "Hozircha fotosuratlar joylanmagan."}
          </p>
        )}
        </div>
      </div>

      <Modal
        open={Boolean(current)}
        title={current?.title || current?.albumTitle || ""}
        onClose={() => setOpen(null)}
        size="xl"
        className="lightbox"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") step(1);
          if (event.key === "ArrowLeft") step(-1);
        }}
        footer={
          visible.length > 1 ? (
            <div className="lightbox-nav">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => step(-1)}>
                <Icon name="chevronRight" size={14} className="flip" />
                Oldingi
              </button>
              <span className="num muted">
                {(open ?? 0) + 1} / {visible.length}
              </span>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => step(1)}>
                Keyingi
                <Icon name="chevronRight" size={14} />
              </button>
            </div>
          ) : undefined
        }
      >
        {current ? (
          <figure className="lightbox-figure">
            {current.kind === "photo" ? (
              <img src={current.full} alt={current.title || current.albumTitle} decoding="async" />
            ) : current.player ? (
              <div className="video-frame">
                <iframe
                  src={current.player}
                  title={current.title || "Video"}
                  allow="encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            ) : (
              <video controls preload="metadata" poster={current.thumb || undefined}>
                <source src={current.full} type="video/mp4" />
                Brauzeringiz videoni qo'llab-quvvatlamaydi.
              </video>
            )}
            {current.caption ? <figcaption>{current.caption}</figcaption> : null}
          </figure>
        ) : null}
      </Modal>
    </section>
  );
}
